import { useState } from 'react';

/**
 * Custom hook for managing drawer state and navigation
 * @param {Object} config - Configuration object containing drawer configuration
 * @param {Object} config.DRAWER_CONFIG - Map of drawer configurations
 * @param {string} initialDrawerType - Initial drawer type to display
 * @param {boolean} initialIsOpen - Whether the drawer should be initially open
 * @returns {Object} Drawer state and handlers
 */
export const useDrawer = (config, initialDrawerType, initialIsOpen = false) => {
  const { DRAWER_CONFIG } = config;

  const [state, setState] = useState({
    isOpen: initialIsOpen,
    type: initialDrawerType,
    data: null,
    title: '',
    isLoading: true,
  });

  const handleOpenDrawer = (type, data, title = '') => {
    setState({
      isOpen: true,
      type,
      data,
      title,
      isLoading: true,
    });
  };

  const handleCloseDrawer = () => {
    setState({
      isOpen: false,
      type: null,
      data: null,
      title: '',
      isLoading: true,
    });
  };

  const handleBackNavigation = () => {
    const currentConfig = DRAWER_CONFIG[state.type];
    if (currentConfig?.parent) {
      setState((prev) => ({
        ...prev,
        type: currentConfig.parent,
        title: '',
      }));
    }
  };

  const renderDrawerContent = () => {
    const drawerConfig = DRAWER_CONFIG[state.type];
    if (!drawerConfig) {
      console.warn(`No drawer configuration found for type: ${state.type}`);
      return null;
    }

    const { component: Component, props: getProps } = drawerConfig;
    const componentProps = getProps({
      drawerData: state.data,
      handleCloseDrawer,
      handleBackNavigation,
      handleOpenDrawer,
      isLoading: state.isLoading,
    });

    return <Component {...componentProps} />;
  };

  return {
    isDrawerOpen: state.isOpen,
    drawerType: state.type,
    drawerData: state.data,
    drawerTitle: state.title,
    isLoading: state.isLoading,
    handleOpenDrawer,
    handleCloseDrawer,
    handleBackNavigation,
    renderDrawerContent,
  };
};
